import { PublicRoutes } from "@/constants/routes";
import { Mail, Scale, ShieldCheck, UserRoundSearch } from "lucide-react";
import { DPO, OMBUDSMAN } from "./company";
import type { LegalDocument } from "./types";

// O e-mail já abre com assunto e roteiro: reclamação formal chega completa,
// e quem escreve não precisa adivinhar o que informar.
const MAILTO = `mailto:${OMBUDSMAN.email}?subject=${encodeURIComponent(
  "Ouvidoria Fidexa - manifestação formal"
)}&body=${encodeURIComponent(
  [
    `Prezado ${OMBUDSMAN.name},`,
    "",
    "Nome completo:",
    "Empresa e CNPJ, se for lojista:",
    "Telefone para contato:",
    "Tipo de manifestação - reclamação, denúncia, sugestão ou elogio:",
    "Número do atendimento anterior, se houver:",
    "",
    "Descrição do ocorrido, com datas:",
    "",
    "O que espero como solução:",
    "",
    "Atenciosamente,",
  ].join("\n")
)}`;

export const OMBUDSMAN_DOC: LegalDocument = {
  key: "ouvidoria",
  path: PublicRoutes.OUVIDORIA,
  eyebrow: "Atendimento",
  title: "Ouvidoria",
  seoTitle: "Ouvidoria: canal independente para reclamações",
  description:
    "A Ouvidoria da Fidexa recebe reclamações não resolvidas, denúncias e sugestões, com análise por um sócio que não atua na operação. Saiba quando e como usar.",
  intro:
    "A Ouvidoria é o canal para quando o atendimento normal não resolveu, ou quando o assunto é sério demais para ser tratado por quem está envolvido nele.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: Scale,
      title: "Avaliação independente",
      text: `Quem responde é ${OMBUDSMAN.name}, sócio que não atua na operação da Fidexa.`,
    },
    {
      icon: UserRoundSearch,
      title: "Encaminhamento a quem resolve",
      text: "A Ouvidoria leva o caso à pessoa responsável e acompanha até a resposta.",
    },
    {
      icon: ShieldCheck,
      title: "Sem retaliação",
      text: "Quem faz uma manifestação de boa-fé não sofre nenhuma consequência por isso.",
    },
    {
      icon: Mail,
      title: "Por e-mail, por escrito",
      text: `Escreva para ${OMBUDSMAN.email}. O registro por escrito protege você e a Fidexa.`,
    },
  ],

  sections: [
    {
      id: "quando-usar",
      title: "Quando procurar a Ouvidoria",
      summary:
        "Depois de falar com o suporte, se o problema não foi resolvido, ou direto, em caso de denúncia.",
      blocks: [
        {
          type: "list",
          items: [
            "**Reclamação não resolvida:** você já falou com o suporte e a resposta não resolveu ou não veio no prazo.",
            "**Denúncia:** conduta inadequada de alguém da Fidexa, uso indevido da plataforma por uma loja, fraude ou desrespeito à lei.",
            "**Sugestão ou elogio:** algo que pode melhorar, ou que merece ser reconhecido.",
          ],
        },
        {
          type: "paragraph",
          text: `Dúvidas do dia a dia vão para o suporte, e pedidos sobre dados pessoais vão para o encarregado de dados, ${DPO.name}. A Ouvidoria não substitui esses canais: ela é a instância seguinte.`,
        },
      ],
    },
    {
      id: "independencia",
      title: "Por que a Ouvidoria é independente",
      summary: `${OMBUDSMAN.name} é ${OMBUDSMAN.role.toLowerCase()} da Fidexa e não participa da operação. Por isso avalia o caso sem ter estado nele.`,
      blocks: [
        {
          type: "paragraph",
          text: `A Ouvidoria fica com **${OMBUDSMAN.name}**, ${OMBUDSMAN.role.toLowerCase()} da Fidexa, que não participa do atendimento, das vendas nem do dia a dia da plataforma. Assim, quem avalia uma reclamação não é quem a causou nem quem já respondeu a ela.`,
        },
        {
          type: "paragraph",
          text: "Ele analisa cada manifestação, leva o caso aos sócios ou às pessoas responsáveis pelo tema e acompanha o tratamento até que haja uma resposta a quem escreveu.",
        },
      ],
    },
    {
      id: "como-escrever",
      title: "Como fazer sua manifestação",
      summary:
        "Mande um e-mail formal com seus dados, o que aconteceu e o que você espera. O botão abaixo já abre o e-mail com o roteiro.",
      blocks: [
        {
          type: "action",
          label: `Escrever para a Ouvidoria`,
          href: MAILTO,
        },
        {
          type: "list",
          ordered: true,
          items: [
            "**Identifique-se:** nome completo, telefone e, se for lojista, a empresa e o CNPJ.",
            "**Diga o tipo:** reclamação, denúncia, sugestão ou elogio.",
            "**Conte o que aconteceu,** com datas e, se houver, o número do atendimento anterior.",
            "**Diga o que espera** como solução.",
            "**Anexe** prints ou documentos que ajudem a entender o caso.",
          ],
        },
        {
          type: "paragraph",
          text: "Confirmamos o recebimento por e-mail. Se faltar alguma informação, pedimos antes de seguir.",
        },
      ],
    },
    {
      id: "sigilo",
      title: "Sigilo e proteção de quem se manifesta",
      summary:
        "Sua manifestação é tratada com sigilo e só é compartilhada com quem precisa para resolver o caso.",
      blocks: [
        {
          type: "paragraph",
          text: "A identidade de quem escreve e o conteúdo da manifestação só são compartilhados com as pessoas necessárias para tratar o caso. Quem se manifesta de boa-fé não sofre nenhuma retaliação, seja cliente, parceiro ou colaborador.",
        },
        {
          type: "paragraph",
          text: `Os dados enviados à Ouvidoria são usados só para tratar a manifestação, nos termos da [Política de Privacidade](${PublicRoutes.POLITICA_PRIVACIDADE}).`,
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
